import React, { useRef, useState } from 'react';
import { Compass, BadgeCheck, Receipt, Users, Check, X, Lock, ClipboardCheck, GraduationCap, Handshake, TrendingUp } from 'lucide-react';
import { Wrapper, ScheduleCountdown, HostRow, TrustStrip, TicketLine, CtaButton, Footer, ContactConsentNote, WEEK_LABEL, H1, H2, LEAD, CARD } from './Shared';
import { LiveHeader } from './LiveHeader';
import { PhoneInput } from './PhoneInput';
import { ProfileCarousel } from './ProfileCarousel';
import { COUNTRIES, Country } from './countries';
import { HOST_PHOTO_URL, BADGE_LEFT } from './config';
import { track, sendLead, saveLead } from './pixel';
const ICON = 'w-7 h-7 text-brand';
const LEARN = [{
  icon: <Compass className={ICON} strokeWidth={1.8} data-id="element-672" data-mp="5jkwv2iyj2z8" />,
  t: 'Qué hacemos y de dónde salen tus clientes',
  p: 'Cómo ayudamos a familias latinas a proteger su dinero, y cómo trabajamos con prospectos que llegan por anuncios. Sin llamadas en frío.'
}, {
  icon: <BadgeCheck className={ICON} strokeWidth={1.8} data-id="element-673" data-mp="elbynwadmpnt" />,
  t: 'El camino exacto a tu licencia',
  p: 'Los pasos de la pre-licencia y el examen en tu estado, y cómo te acompañamos para que no lo hagas sola o solo.'
}, {
  icon: <Receipt className={ICON} strokeWidth={1.8} data-id="element-674" data-mp="1wan29vo4g9jz" />,
  t: 'Costos y requisitos reales',
  p: 'Lo que cuesta empezar y lo que necesitas, explicado con claridad antes de que tomes cualquier decisión.'
}, {
  icon: <Users className={ICON} strokeWidth={1.8} data-id="element-675" data-mp="1mflfh6hbjjkk" />,
  t: 'Cómo crecer y formar tu equipo',
  p: 'Cómo se construye esto poco a poco, a tu ritmo, cuando tus resultados lo permitan.'
}];

// El método de Chale: acompañamiento etapa por etapa (el orden importa)
const GUIDE = [{
  icon: ClipboardCheck,
  t: 'Diagnóstico personal',
  p: 'En una llamada de 30 minutos revisamos tu situación, tu tiempo y tus metas. Te digo con honestidad si esto es para ti y cuál sería tu camino.'
}, {
  icon: GraduationCap,
  t: 'Licencia, con plan y seguimiento',
  p: 'Te doy un plan de estudio para la pre-licencia y el examen de tu estado, y reviso tu avance cada semana. Si ya tienes licencia, pasamos directo al siguiente paso.'
}, {
  icon: Handshake,
  t: 'Tus primeras citas, acompañada o acompañado',
  p: 'Trabajas con prospectos que llegan por anuncios. En tus primeras citas estoy contigo: primero me ves a mí, después lo haces tú con mi apoyo.'
}, {
  icon: TrendingUp,
  t: 'Crecimiento y liderazgo',
  p: 'Cuando tus resultados lo permitan, te enseño a formar y guiar tu propio equipo con el mismo método.'
}];
const MOST = ['Entran a un equipo sin sistema y aprenden solos, a prueba y error', 'Sin prospectos: les toca llamar a familiares y amigos', 'Nadie los acompaña después de sacar la licencia', 'No saben cuál es su lugar dentro del equipo'];
const WITH_CHALE = ['Un sistema paso a paso: del entrenamiento a tu primera cita', 'Te proveemos prospectos interesados que llegan por anuncios', 'Te acompañamos en tus primeras citas hasta que te sientas segura o seguro', 'Encuentras tu lugar en el equipo según tu perfil y tu tiempo'];
const FIT = ['Quieres construir algo propio y estás dispuesto/a a aprender', 'Tienes permiso de trabajo vigente en EE.UU.', 'Puedes dedicarle al menos 5–10 horas por semana', 'Estás dispuesto/a a sacar la licencia estatal, o ya la tienes y buscas un equipo donde encajar'];
const NOT_FIT = ['Buscas dinero rápido o ingresos garantizados', 'Buscas un empleo con salario fijo', 'No quieres capacitarte ni cumplir requisitos', 'No puedes conectarte desde un lugar tranquilo'];
function validPhone(digits: string, c: Country) {
  const d = digits.replace(/\D/g, '');
  return d.length >= c.min && d.length <= c.max;
}
const SECTION = 'py-16 sm:py-24';
const input = 'w-full rounded-xl border border-gray-200 bg-white px-5 py-4 sm:py-[18px] text-[16px] sm:text-[18px] text-ink outline-none transition-[border-color,box-shadow] duration-150 ease-out focus:border-brand focus:ring-4 focus:ring-brand-light placeholder:text-gray-400';
const PORTRAIT_SHADOW = 'shadow-[0_30px_60px_-30px_rgba(11,13,18,0.45)]';

// Listas con ícono: el título se centra en móvil, los renglones quedan alineados a la izquierda para leerse rápido
function CheckList({
  items,
  tone
}: {
  items: string[];
  tone: 'muted' | 'brand' | 'onBrand';
}) {
  const txt = tone === 'onBrand' ? 'text-white' : tone === 'muted' ? 'text-gray-500' : 'text-ink';
  const dot = tone === 'onBrand' ? 'bg-white text-brand' : tone === 'muted' ? 'bg-gray-200 text-gray-500' : 'bg-brand text-white';
  const Icon = tone === 'muted' ? X : Check;
  return <ul className="mx-auto mt-5 max-w-[420px] space-y-4 text-left md:mx-0" data-id="element-676" data-mp="tyige4nzcsxt">
      {items.map(m => <li key={m} className={`flex items-start gap-3 text-[15px] sm:text-[17px] leading-snug ${txt}`} data-id="element-677" data-mp="xy93nyeasqxl">
          <span className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${dot}`} data-id="element-678" data-mp="12qkc5pn3tl1r">
            <Icon className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" data-id="element-679" data-mp="67mjit4o37jp" />
          </span>
          {m}
        </li>)}
    </ul>;
}
export function Registration({
  onSuccess
}: {
  onSuccess: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState<Country>(COUNTRIES[0]);
  const [err, setErr] = useState('');
  const [sending, setSending] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
    setTimeout(() => nameRef.current?.focus({
      preventScroll: true
    }), 500);
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validPhone(phone, country)) {
      setErr('Por favor ingresa un número válido.');
      return;
    }
    setErr('');
    setSending(true);
    const e164 = `+${country.dial}${phone.replace(/\D/g, '')}`;
    saveLead({
      name,
      email,
      phone: e164
    });
    await sendLead({
      step: 'datos',
      name,
      email,
      phone: e164,
      country: country.iso.toUpperCase(),
      source: window.location.href,
      contact_disclosure_at: new Date().toISOString()
    });
    track('Lead');
    setSending(false);
    onSuccess();
  };
  return <div className="funnel-root bg-white" data-id="element-680" data-mp="28lw8y1oln6vl">
      {/* Barra con punto dorado intermitente (agenda abierta). Reemplaza la pastilla que repetía "1:1". */}
      <LiveHeader data-id="element-681" data-mp="2dma80u8836hh" />

      {/* HERO */}
      <section className="pt-8 sm:pt-14 lg:pt-16 pb-10 sm:pb-12" data-id="element-682" data-mp="3198lrk4yk0bp">
        <Wrapper data-id="element-683" data-mp="31c4ij552kiyk">
          {/* 1) Mensaje a todo el ancho: el título respira en 2 líneas en desktop */}
          <div className="flex flex-col items-center text-center" data-id="element-684" data-mp="2rjyrpyqk07ky">
            {/* Móvil: quién te habla, centrado */}
            <div className="lg:hidden mb-6 flex flex-col items-center gap-2.5" data-id="element-685" data-mp="2wijr5vxzen8c">
              {HOST_PHOTO_URL && <img src={HOST_PHOTO_URL} alt="Chale Rodriguez" width={64} height={64} decoding="async" className="h-16 w-16 rounded-full object-cover object-[50%_18%] ring-[3px] ring-white outline outline-1 outline-gold/60 shadow-[0_10px_24px_-10px_rgba(11,13,18,0.45)]" data-id="element-686" data-mp="3k7l51ypl2ql0" />}
              <div className="text-center" data-id="element-687" data-mp="18khn6p4y3zu9">
                <p className="font-bold text-ink text-[15px] leading-tight" data-id="element-688" data-mp="18jg90bsmux8b">Chale Rodriguez</p>
                <p className="mt-0.5 text-[13px] text-gray-500" data-id="element-689" data-mp="1nc002e2ide9e">Latina emprendedora · Te guía paso a paso</p>
              </div>
            </div>

            <h1 className={`${H1} max-w-[1000px] lg:text-[68px] lg:leading-[1.04]`} data-id="element-690" data-mp="ukq5adbb5zaz">
              Construye tu propia <span className="text-brand" data-id="element-691" data-mp="pngfgwk0blmm">cartera de clientes</span> en finanzas
            </h1>

            <p className="mt-5 lg:mt-6 max-w-[860px] text-[19px] sm:text-[24px] lg:text-[28px] font-bold leading-snug text-ink [text-wrap:balance]" data-id="element-692" data-mp="13n914dgofbnq">
              Nosotros invertimos en los anuncios. <span className="hl-yellow" data-id="element-693" data-mp="2f0lcd9ermwjx">Tú te enfocas en atender y cerrar.</span>
            </p>

            <p className={`${LEAD} mt-4 max-w-[720px] [text-wrap:balance]`} data-id="element-694" data-mp="2b0j7yshnbp0y">
              Sin experiencia previa y sin llamadas en frío. Ayudas a familias latinas a proteger su dinero y yo te guío paso a paso: licencia, entrenamiento y tus primeras citas.
            </p>

            <p className="mt-5 flex w-fit max-w-full items-center justify-center gap-2 rounded-2xl sm:rounded-full bg-brand-light px-4 py-2 text-center text-[14px] sm:text-[15px] font-semibold text-brand" data-id="element-695" data-mp="iba3e9565vye">
              <BadgeCheck className="h-4 w-4 shrink-0" strokeWidth={2.2} aria-hidden="true" data-id="element-696" data-mp="r3u73o0biqq3" />
              <span data-id="element-697" data-mp="w2eyqz1i822x">¿Ya tienes licencia? También hay lugar para ti.</span>
            </p>
          </div>

          {/* 2) Formulario + retrato, alineados arriba (sin huecos) */}
          <div className="mt-10 lg:mt-14 grid items-start gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14" data-id="element-698" data-mp="10zxa0w2zute3">
            <div ref={formRef} className={`${CARD} mx-auto w-full max-w-[620px] p-6 sm:p-9 text-left lg:mx-0 lg:max-w-none`} data-id="element-699" data-mp="15yi8apyor2zw">
              <p className="text-center text-[14px] sm:text-[15px] font-semibold text-brand" data-id="element-700" data-mp="37rppk4xuzuof">{WEEK_LABEL}</p>
              <h2 className="mt-2 text-center font-extrabold text-ink tracking-tight text-[22px] sm:text-[27px] leading-tight [text-wrap:balance]" data-id="element-701" data-mp="1oxd0pe5b9gj4">
                ¿A dónde te envío el acceso para elegir tu horario?
              </h2>

              <form id="regForm" onSubmit={submit} className="mt-6 space-y-3.5 sm:space-y-4" data-id="element-702" data-mp="1g51ey5l56h9h">
                <label htmlFor="reg-name" className="sr-only" data-id="element-703" data-mp="1b79kb3t44h1l">Nombre completo</label>
                <input id="reg-name" ref={nameRef} className={input} type="text" name="name" placeholder="Nombre completo" required autoComplete="name" value={name} onChange={e => setName(e.target.value)} data-id="element-704" data-mp="27uo3h8q5is3b" />
                <label htmlFor="reg-email" className="sr-only" data-id="element-705" data-mp="1ytpwchkf24gi">Correo electrónico</label>
                <input id="reg-email" className={input} type="email" name="email" placeholder="Correo electrónico" required autoComplete="email" inputMode="email" value={email} onChange={e => setEmail(e.target.value)} data-id="element-706" data-mp="1tvemi6u4ftya" />
                <PhoneInput value={phone} onChange={v => {
                setPhone(v);
                if (err) setErr('');
              }} country={country} onCountryChange={setCountry} error={err} data-id="element-707" data-mp="ifq13jwin2zl" />
              </form>

              <div className="mt-6" data-id="element-708" data-mp="b46llupiqfoa">
                <CtaButton type="submit" form="regForm" disabled={sending} label={sending ? 'Reservando tu lugar…' : 'Reservar mi lugar con Chale'} sub="Después eliges el día y la hora" data-id="element-709" data-mp="b10w4ecz9bs1" />
              </div>
              <p className="mt-4 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center text-[13px] text-gray-500" data-id="element-710" data-mp="yreok5zh5cn5">
                <Lock className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden="true" data-id="element-711" data-mp="tt3atk626hzn" />
                <span data-id="element-712" data-mp="18puggb6bspxp">Paso 1 de 2 · Menos de 60 segundos · Sin spam</span>
              </p>
              <ContactConsentNote className="mt-4 text-center" data-id="element-713" data-mp="13s2e0n821lf9" />
            </div>

            {/* Desktop: retrato grande junto al formulario */}
            {HOST_PHOTO_URL && <figure className="relative mx-auto hidden w-full max-w-[460px] lg:block" data-id="element-714" data-mp="35jxkmofgezqm">
                <div aria-hidden="true" className="absolute inset-0 translate-x-[14px] translate-y-[14px] rounded-[28px] border-[1.5px] border-gold" data-id="element-715" data-mp="1l0lf3v7ht0rw" />
                <div className={`relative aspect-[4/5] overflow-hidden rounded-[28px] bg-brand-light ${PORTRAIT_SHADOW}`} data-id="element-716" data-mp="1q2139w4yvnei">
                  <img src={HOST_PHOTO_URL} alt="Chale Rodriguez" width={920} height={1150} decoding="async" className="h-full w-full object-cover object-[50%_14%]" data-id="element-717" data-mp="1g9vbonimn7zu" />
                </div>
                <span className="absolute left-5 top-5 rounded-full bg-white/95 px-3.5 py-1.5 text-[13px] font-bold text-ink shadow-[0_8px_20px_-10px_rgba(11,13,18,0.35)]" data-id="element-718" data-mp="16cgu2kkywd5y">
                  <span className="text-brand" data-id="element-719" data-mp="seiq8949vz4p">{BADGE_LEFT.value}</span> en {BADGE_LEFT.title}
                </span>
                <figcaption className={`absolute -bottom-7 left-6 right-6 rounded-2xl bg-white px-5 py-4 ${CARD}`} data-id="element-720" data-mp="2mqgs1q095sqk">
                  <p className="font-extrabold text-ink text-[17px] leading-tight" data-id="element-721" data-mp="2rpjx3ag8fyyj">Chale Rodriguez</p>
                  <p className="mt-0.5 text-[14px] text-gray-500" data-id="element-722" data-mp="2q8ehnuckg0fc">Latina emprendedora · Te guía paso a paso</p>
                </figcaption>
              </figure>}
          </div>

          <div className="lg:mt-10" data-id="element-723" data-mp="2g9f90brsw4sc">
            <ScheduleCountdown data-id="element-724" data-mp="2gagula6g09ob" />
          </div>
          <TrustStrip data-id="element-725" data-mp="2bb3zy39bxyb6" />
        </Wrapper>
      </section>

      {/* EL MÉTODO */}
      <section className="pt-12 sm:pt-16 pb-16 sm:pb-24 bg-[#F7F8FB]" data-id="element-726" data-mp="2bd8d7bt4c9o4">
        <Wrapper data-id="element-727" data-mp="38xulorngmb0i">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16" data-id="element-728" data-mp="390gsgp2j0mho">
            <div className="text-center lg:text-left lg:sticky lg:top-10 lg:self-start" data-id="element-729" data-mp="3401ebvaf7ayn">
              <p className="font-semibold text-brand text-[14px] sm:text-[15px]" data-id="element-730" data-mp="2z78cbf6c6gq9">El método de Chale</p>
              <h2 className={`${H2} lg:text-left mt-3`} data-id="element-731" data-mp="2u96sgs136ddq">Te guío en cada paso, <span className="text-brand" data-id="element-732" data-mp="3rpnbyeb59i4j">sin soltarte de la mano</span></h2>
              <p className={`${LEAD} mt-5 max-w-[560px] mx-auto lg:mx-0 [text-wrap:balance]`} data-id="element-733" data-mp="3mydsjvg6vb13">
                No te doy un manual para que te las arregles sola o solo. Desde el primer día sabes qué hacer, por qué y con quién contar.
              </p>
              <figure className={`${CARD} mt-8 p-6 sm:p-7 text-center lg:text-left max-w-[560px] mx-auto lg:mx-0`} data-id="element-734" data-mp="33r1h9goari5">
                <blockquote className="text-[17px] sm:text-[19px] font-semibold leading-snug text-ink [text-wrap:balance]" data-id="element-735" data-mp="810q4mw8mz4x">
                  “Mi trabajo es que nunca te sientas perdida o perdido. Cuando tengas una duda, me escribes y la resolvemos juntos.”
                </blockquote>
                <figcaption className="mt-5 flex items-center justify-center gap-3 lg:justify-start" data-id="element-736" data-mp="m0jvj7xnzwdj">
                  {HOST_PHOTO_URL && <img src={HOST_PHOTO_URL} alt="" width={44} height={44} loading="lazy" decoding="async" className="h-11 w-11 rounded-full object-cover object-[50%_18%]" data-id="element-737" data-mp="vrmvu7irs7br" />}
                  <span className="text-left" data-id="element-738" data-mp="15q3rhhsnla45">
                    <span className="block font-bold text-[15px] text-ink leading-tight" data-id="element-739" data-mp="15qvnuuu2rbvh">Chale Rodriguez</span>
                    <span className="block text-[13px] text-gray-500" data-id="element-740" data-mp="22fwedsx1gk44">Tu guía en este camino</span>
                  </span>
                </figcaption>
              </figure>
            </div>

            {/* Móvil: pasos centrados unidos por una línea dorada · Desktop: línea de tiempo lateral */}
            <ol className="relative" data-id="element-741" data-mp="278phkccuzke3">
              {GUIDE.map(({
              icon: Icon,
              t,
              p
            }, i) => <li key={t} className="relative flex flex-col items-center gap-4 pb-14 text-center last:pb-0 lg:flex-row lg:items-start lg:gap-5 lg:pb-10 lg:text-left" data-id="element-742" data-mp="1jj4xfq4yyh38" data-mpi={i}>
                  {i < GUIDE.length - 1 && <span className="absolute left-6 top-14 bottom-0 hidden w-px bg-gray-200 lg:block" aria-hidden="true" data-id="element-743" data-mp="1sk2vzswn1wef" />}
                  {i < GUIDE.length - 1 && <span className="absolute bottom-3 left-1/2 h-7 w-px -translate-x-1/2 bg-gold/70 lg:hidden" aria-hidden="true" data-id="element-744" data-mp="33ygfklkczpon" />}
                  <span className="relative z-10 inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand text-white ring-4 ring-white shadow-[0_10px_24px_-10px_rgba(11,13,18,0.45)]" data-id="element-745" data-mp="2zvtgbzlcz2hf">
                    <Icon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-746" data-mp="3cg8kdw734yin" />
                  </span>
                  <div className="max-w-[440px] lg:pt-0.5" data-id="element-747" data-mp="2xjhqu3k3eh0y">
                    <p className="text-[13px] font-semibold uppercase tracking-wider text-gold" data-id="element-748" data-mp="32grfhgznqonq">Paso {i + 1}</p>
                    <h3 className="mt-1 font-bold text-[19px] sm:text-[22px] leading-snug text-ink [text-wrap:balance]" data-id="element-749" data-mp="3q6lt40rswh91">{t}</h3>
                    <p className="mt-2 text-[15px] sm:text-[16px] leading-relaxed text-gray-600" data-id="element-750" data-mp="3v5f6zub02a9q">{p}</p>
                  </div>
                </li>)}
            </ol>
          </div>

          <div className="mt-14" data-id="element-751" data-mp="3leu8e6vbt5qt"><CtaButton onClick={scrollToForm} label="Quiero mi plan de 90 días" sub="Lo armamos juntos en tu llamada 1:1" data-id="element-752" data-mp="3le2f52p7bk3p" /></div>
        </Wrapper>
      </section>

      {/* PERFILES ILUSTRATIVOS */}
      <section className={SECTION} data-id="element-753" data-mp="ht52cl9gykfl">
        <Wrapper className="text-center" data-id="element-754" data-mp="rmskm8g1klz8">
          <h2 className={H2} data-id="element-755" data-mp="rmiqq9rvluo9">¿Te identificas con <span className="text-brand" data-id="element-756" data-mp="mny1f5e5wvel">alguna de estas historias?</span></h2>
          <p className={`${LEAD} mt-5 max-w-[600px] mx-auto [text-wrap:balance]`} data-id="element-757" data-mp="mor1xomj2ooa">
            Latinos y latinas que buscan construir algo propio, a su ritmo.
          </p>

          <ProfileCarousel data-id="element-758" data-mp="dmhr0xncktad" />

          <p className="mt-14 max-w-[760px] mx-auto font-bold text-[19px] sm:text-[24px] leading-snug text-ink [text-wrap:balance]" data-id="element-759" data-mp="dpdmmfbq35vn">
            En nuestra llamada te muestro el método completo paso a paso para que decidas <span className="hl-yellow" data-id="element-760" data-mp="3vn5jvhxdxpz">con toda la información.</span>
          </p>
          <div className="mt-8" data-id="element-761" data-mp="3v1oi68hjgzdq"><CtaButton onClick={scrollToForm} label="Quiero ver el método completo" sub="30 minutos, solo tú y Chale" data-id="element-762" data-mp="3r25z7i9a2ydj" /></div>
        </Wrapper>
      </section>

      {/* EQUIPO + SISTEMA + PROSPECTOS */}
      <section className={`${SECTION} bg-[#F7F8FB]`} data-id="element-763" data-mp="3h3zuksw37e4z">
        <Wrapper data-id="element-764" data-mp="3h4i2wnw0xit9">
          <h2 className={H2} data-id="element-765" data-mp="3h62waa6lqvpj">Muchos empiezan solos. <span className="text-brand" data-id="element-766" data-mp="1wmh0ds9oonkl">Aquí tienes equipo, sistema y prospectos.</span></h2>
          <p className={`${LEAD} mt-5 max-w-[720px] mx-auto text-center [text-wrap:balance]`} data-id="element-767" data-mp="21ksbe6bpt44e">
            La mayoría entra a este negocio y descubre que su equipo no tiene sistema ni prospectos. <span className="font-semibold text-ink" data-id="element-768" data-mp="2alqd2hyokzjt">Conmigo, despreocúpate de eso.</span>
          </p>

          <div className="mt-12 grid gap-5 md:grid-cols-2" data-id="element-769" data-mp="1dvfz2xl3cfxv">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-9 text-center md:text-left" data-id="element-770" data-mp="1dxanwmzhgs78">
              <h3 className="font-extrabold text-gray-500 text-[17px] sm:text-[19px]" data-id="element-771" data-mp="1isg8nih5g0tq">Lo que vive la mayoría</h3>
              <CheckList items={MOST} tone="muted" data-id="element-772" data-mp="1mub6ue3s9y3a" />
            </div>
            <div className="rounded-2xl bg-brand p-6 sm:p-9 text-center md:text-left text-white shadow-[0_24px_50px_-24px_rgba(11,13,18,0.45)]" data-id="element-773" data-mp="1rofj9cvtiybr">
              <h3 className="font-extrabold text-[19px] sm:text-[22px]" data-id="element-774" data-mp="z6bkrrhf2vv1">En el equipo de Chale</h3>
              <CheckList items={WITH_CHALE} tone="onBrand" data-id="element-775" data-mp="3oodd4d4aplqe" />
            </div>
          </div>
          <p className="mt-5 text-center text-[12px] text-gray-500 [text-wrap:balance]" data-id="element-776" data-mp="3jpk2qtkb2aug">
            La cantidad de prospectos depende de la actividad del equipo, la inversión publicitaria y tu disponibilidad. No garantiza citas, ventas ni ingresos.
          </p>

          <div className="mt-10" data-id="element-777" data-mp="3tmeyj4p71x51"><CtaButton onClick={scrollToForm} label="Quiero entrar al equipo de Chale" sub="En la llamada vemos si encajas" data-id="element-778" data-mp="3tq2qw0iwkdi6" /></div>
        </Wrapper>
      </section>

      {/* LEARN */}
      <section className={SECTION} data-id="element-779" data-mp="6gxquhgeb3dz">
        <Wrapper data-id="element-780" data-mp="l7n1nipy2ghb">
          <h2 className={H2} data-id="element-781" data-mp="l86eil0qmeoh">Esto es lo que vas a descubrir en la llamada</h2>
          <div className="mt-12 grid gap-5 sm:gap-6 sm:grid-cols-2" data-id="element-782" data-mp="g93drcrsiumb">
            {LEARN.map(({
            icon,
            t,
            p
          }) => <article key={t} className={`${CARD} p-6 sm:p-8 text-center sm:text-left`} data-id="element-783" data-mp="u7tgb7xdpmi2">
                <span className="mx-auto sm:mx-0 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-light" data-id="element-784" data-mp="q7j61j2k09xe">{icon}</span>
                <h3 className="mt-5 font-bold text-[19px] sm:text-[22px] leading-snug text-ink [text-wrap:balance]" data-id="element-785" data-mp="18vti155fgzdg">{t}</h3>
                <p className="mt-2 text-[15px] sm:text-[16px] leading-relaxed text-gray-600" data-id="element-786" data-mp="1ds0ocz1qkcvx">{p}</p>
              </article>)}
          </div>
        </Wrapper>
      </section>

      {/* ¿ES PARA TI? */}
      <section className={`${SECTION} bg-[#F7F8FB]`} data-id="element-787" data-mp="q4sj2h4b53tw">
        <Wrapper data-id="element-788" data-mp="q3920oatuor2">
          <h2 className={H2} data-id="element-789" data-mp="q2phcztslm9c">Esta llamada <span className="text-brand" data-id="element-790" data-mp="uv8ww2c1irgs">no es para todo el mundo</span></h2>
          <p className={`${LEAD} mt-5 max-w-[640px] mx-auto text-center [text-wrap:balance]`} data-id="element-791" data-mp="uxd9d8o08yso">
            <span className="typ-signature inline-block -rotate-3 align-[-0.12em] mr-1.5 text-[1.7em] leading-none text-brand" data-id="element-792" data-mp="h2162cvsa005">CHALE</span>
            acompaña personalmente a cada persona que guía. Por eso abre pocos espacios por semana.
          </p>

          <div className={`${CARD} mt-12 grid overflow-hidden md:grid-cols-2`} data-id="element-793" data-mp="c3zknlavxoli">
            <div className="p-6 sm:p-9 text-center md:text-left" data-id="element-794" data-mp="b945vtwx5jtp">
              <h3 className="font-extrabold text-ink text-[19px] sm:text-[22px]" data-id="element-795" data-mp="bc02nex162gk">Es para ti si…</h3>
              <CheckList items={FIT} tone="brand" data-id="element-796" data-mp="f8w53g0fadme" />
            </div>
            <div className="border-t border-gray-100 bg-[#F7F8FB] p-6 sm:p-9 text-center md:text-left md:border-l md:border-t-0" data-id="element-797" data-mp="1d0qvngyxqpc">
              <h3 className="font-extrabold text-ink text-[19px] sm:text-[22px]" data-id="element-798" data-mp="6cdo925gug66">No es para ti si…</h3>
              <CheckList items={NOT_FIT} tone="muted" data-id="element-799" data-mp="u2hjom8i39m3" />
            </div>
          </div>
        </Wrapper>
      </section>

      {/* FINAL CTA */}
      <section className={SECTION} data-id="element-800" data-mp="k9afv5tes1o4">
        <Wrapper className="text-center" data-id="element-801" data-mp="k87pcz00018q">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2 text-[13px] sm:text-[15px] font-bold uppercase tracking-wider text-white" data-id="element-802" data-mp="p8vbb69ddrw3">
            <span className="h-1.5 w-1.5 rounded-full bg-gold" aria-hidden="true" data-id="element-803" data-mp="1cx6920jrrbg5" />
            Diagnóstico personal 1:1
          </div>
          <h2 className={`${H2} mt-6`} data-id="element-804" data-mp="32flbts1gycca">Sal de la llamada con <span className="text-brand" data-id="element-805" data-mp="32e0e5tjuyda7">tu plan de los primeros 90 días</span></h2>
          <p className={`${LEAD} mt-5 max-w-[680px] mx-auto [text-wrap:balance]`} data-id="element-806" data-mp="2xgiektrqdaev">
            En 30 minutos revisamos tu situación, tu tiempo y tus metas. Si agendas <span className="font-semibold text-brand" data-id="element-807" data-mp="376bdovaorx28">esta semana</span>, te llevas <span className="hl-yellow font-semibold text-ink" data-id="element-808" data-mp="3797celucyprq">qué hacer cada semana</span>, desde la licencia hasta tus primeras citas.
          </p>
          <ScheduleCountdown data-id="element-809" data-mp="2ej7hnftkmt2l" />
          <HostRow data-id="element-810" data-mp="2ek0cpjj63u4w" />
          <div className="mt-10" data-id="element-811" data-mp="2ekik9cba5us4"><CtaButton onClick={scrollToForm} label="Apartar mi llamada de esta semana" sub="La agenda cierra el domingo a las 11:59 PM" data-id="element-812" data-mp="2sigk7coutoo3" /></div>
          <div className="mt-5" data-id="element-813" data-mp="2nharhev721ek"><TicketLine data-id="element-814" data-mp="2nkylsg8gqruc" /></div>
        </Wrapper>
      </section>

      <Footer data-id="element-815" data-mp="7qeraxlikcnm" />
    </div>;
}